import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useEditor, EditorContent, BubbleMenu, FloatingMenu } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import TextStyle from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import TextAlign from '@tiptap/extension-text-align'
import FontFamily from '@tiptap/extension-font-family'
import CharacterCount from '@tiptap/extension-character-count'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import Underline from '@tiptap/extension-underline'
import Superscript from '@tiptap/extension-superscript'
import Subscript from '@tiptap/extension-subscript'
import Placeholder from '@tiptap/extension-placeholder'
import { PaginationPlus, PAGE_SIZES } from 'tiptap-pagination-plus'
import SearchAndReplace from '@sereneinserenade/tiptap-search-and-replace'
import { Extension, InputRule } from '@tiptap/core'
import { PlaceholderHighlight, countPlaceholders } from './PlaceholderHighlight'

import {
  Box,
  Paper,
  Toolbar,
  Button,
  ButtonGroup,
  IconButton,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  Chip,
  Typography,
  Alert,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Menu,
  MenuList,
  ListItemIcon,
  Slider,
  FormControlLabel,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  Stack
} from '@mui/material'

import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  TableChart as TableIcon,
  Save,
  FileDownload,
  ViewModule as Template,
  Gavel,
  FormatColorText,
  FormatListBulleted,
  FormatListNumbered,
  FormatIndentIncrease,
  FormatIndentDecrease,
  Title as TitleIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  Highlight as HighlightIcon,
  FormatQuote,
  Undo,
  Redo,
  FindReplace,
  StrikethroughS,
  FormatClear,
  Add as AddIcon,
  ZoomIn,
  ZoomOut,
  AspectRatio,
  Crop,
  Delete as DeleteIcon,
  Star as StarIcon,
  Edit as EditImageIcon,
  Description as DescriptionIcon,
  FormatLineSpacing,
  InsertPageBreak,
  Close as CloseIcon,
  NavigateNext,
  NavigateBefore,
  SwapHoriz,
  FormatTextdirectionLToR as IndentIcon,
  SpaceBar as ParagraphSpacingIcon,
  PrintOutlined,
  Toc as TocIcon,
  Superscript as SuperscriptIcon,
  Subscript as SubscriptIcon,
  MoreHoriz as SpecialCharsIcon,
  AccountTree as OutlineIcon,
  CenterFocusStrong as FocusModeIcon,
  TextSnippet as SnippetsIcon,
  CalendarMonth as DateIcon,
  AttachMoney as MoneyIcon,
  WaterDrop as WatermarkIcon,
  Draw as SignatureIcon,
  FormatListNumberedRtl as LegalNumberIcon,
  Flag as TargetIcon,
} from '@mui/icons-material'

import api from '../../services/api'
import Ruler, { VerticalRuler, RULER_WIDTH } from './Ruler'
import FieldsPanel from './FieldsPanel'
import SimplifiedJurisprudencePanel from './SimplifiedJurisprudencePanel'
import AIRewritePopover from './AIRewritePopover'
import AIFullRewriteDialog from './AIFullRewriteDialog'
import DocumentOutline from './DocumentOutline'
import LegalSnippetsPanel from './LegalSnippetsPanel'
import { valorPorExtenso, parseMonetaryValue } from '../../utils/valorExtenso'
import { AutoAwesome as AIIcon } from '@mui/icons-material'

const FontSizeExtension = Extension.create({
  name: 'fontSize',
  addGlobalAttributes() {
    return [{
      types: ['textStyle'],
      attributes: {
        fontSize: {
          default: null,
          parseHTML: (el: HTMLElement) => el.style.fontSize || null,
          renderHTML: (attrs: Record<string, any>) => {
            if (!attrs.fontSize) return {}
            return { style: `font-size: ${attrs.fontSize}` }
          },
        },
      },
    }]
  },
  addCommands(): any {
    return {
      setFontSize: (size: string) => ({ chain }: any) => chain().setMark('textStyle', { fontSize: size }).run(),
      unsetFontSize: () => ({ chain }: any) => chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
    }
  },
})

const LineSpacingExtension = Extension.create({
  name: 'lineSpacing',
  addGlobalAttributes() {
    return [{
      types: ['paragraph', 'heading'],
      attributes: {
        lineHeight: {
          default: null,
          parseHTML: (el: HTMLElement) => el.style.lineHeight || null,
          renderHTML: (attrs: Record<string, any>) => {
            if (!attrs.lineHeight) return {}
            return { style: `line-height: ${attrs.lineHeight}` }
          },
        },
      },
    }]
  },
  addCommands(): any {
    return {
      setLineSpacing: (spacing: string) => ({ commands }: any) => commands.updateAttributes('paragraph', { lineHeight: spacing }),
    }
  },
})

const ParagraphSpacingExtension = Extension.create({
  name: 'paragraphSpacing',
  addGlobalAttributes() {
    return [{
      types: ['paragraph'],
      attributes: {
        marginBottom: {
          default: null,
          parseHTML: (el: HTMLElement) => el.style.marginBottom || null,
          renderHTML: (attrs: Record<string, any>) => {
            if (!attrs.marginBottom) return {}
            return { style: `margin-bottom: ${attrs.marginBottom}` }
          },
        },
        textIndent: {
          default: null,
          parseHTML: (el: HTMLElement) => el.style.textIndent || null,
          renderHTML: (attrs: Record<string, any>) => {
            if (!attrs.textIndent) return {}
            return { style: `text-indent: ${attrs.textIndent}` }
          },
        },
      },
    }]
  },
  addCommands(): any {
    return {
      setParagraphSpacing: (spacing: string) => ({ commands }: any) => commands.updateAttributes('paragraph', { marginBottom: spacing }),
      setTextIndent: (indent: string) => ({ commands }: any) => commands.updateAttributes('paragraph', { textIndent: indent }),
    }
  },
})

const SmartTypographyExtension = Extension.create({
  name: 'smartTypography',
  addInputRules() {
    return [
      new InputRule({ find: /--$/, handler: ({ state, range }: any) => { state.tr.replaceWith(range.from, range.to, state.schema.text('—')) } }),
      new InputRule({ find: /\.\.\.$/, handler: ({ state, range }: any) => { state.tr.replaceWith(range.from, range.to, state.schema.text('…')) } }),
      new InputRule({ find: /\(c\)$/i, handler: ({ state, range }: any) => { state.tr.replaceWith(range.from, range.to, state.schema.text('©')) } }),
      new InputRule({ find: /\(r\)$/i, handler: ({ state, range }: any) => { state.tr.replaceWith(range.from, range.to, state.schema.text('®')) } }),
      new InputRule({ find: /\(tm\)$/i, handler: ({ state, range }: any) => { state.tr.replaceWith(range.from, range.to, state.schema.text('™')) } }),
    ]
  },
})

interface SimpleLegalEditorProps {
  content: string
  onContentChange: (content: string) => void
  onSave?: () => void
  documentId?: string
  readOnly?: boolean
  onFeedbackRequest?: (selectedText: string, context: string) => void
}

interface JurisprudenceResult {
  id: string
  titulo: string
  tribunal: string
  numero_processo?: string
  relator?: string
  data_julgamento?: string
  ementa: string
  inteiro_teor?: string
  relevancia?: number
}

const PageCounter: React.FC<{ editorContainer: HTMLElement | null }> = ({ editorContainer }) => {
  const [totalPages, setTotalPages] = useState(1)
  useEffect(() => {
    if (!editorContainer) return
    const measure = () => {
      const footers = editorContainer.querySelectorAll('.rm-page-footer')
      setTotalPages(Math.max(1, footers.length))
    }
    measure()
    const obs = new MutationObserver(measure)
    obs.observe(editorContainer, { childList: true, subtree: true })
    return () => obs.disconnect()
  }, [editorContainer])

  return (
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
      {totalPages} {totalPages === 1 ? 'página' : 'páginas'}
    </Typography>
  )
}

const SimpleLegalEditor: React.FC<SimpleLegalEditorProps> = ({
  content,
  onContentChange,
  onSave,
  documentId,
  readOnly = false,
  onFeedbackRequest
}) => {
  // Contagem de placeholders mantida em estado (atualizada de forma debounced no
  // onUpdate), evitando editor.getHTML() a cada render da status bar.
  const [placeholderCount, setPlaceholderCount] = useState(0)
  const updateDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [jurisprudenceDialog, setJurisprudenceDialog] = useState(false) // Agora controla painel lateral
  const [jurisprudenceSearch, setJurisprudenceSearch] = useState('')
  const [jurisprudenceResults, setJurisprudenceResults] = useState<JurisprudenceResult[]>([])
  const [notification, setNotification] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info'
  })
  
  // Estados para ferramentas avançadas
  const [fontMenuAnchor, setFontMenuAnchor] = useState<null | HTMLElement>(null)
  const [sizeMenuAnchor, setSizeMenuAnchor] = useState<null | HTMLElement>(null)
  const [colorMenuAnchor, setColorMenuAnchor] = useState<null | HTMLElement>(null)
  const [currentFontSize, setCurrentFontSize] = useState('12pt')
  const [currentFontFamily, setCurrentFontFamily] = useState('Times New Roman')
  
  // Estado para zoom
  const [zoom, setZoom] = useState(100)
  
  // Estado para buscar e substituir
  const [showFindReplace, setShowFindReplace] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [replaceTerm, setReplaceTerm] = useState('')
  const [showReplace, setShowReplace] = useState(false)

  // Estados para menus de espaçamento
  const [lineSpacingAnchor, setLineSpacingAnchor] = useState<null | HTMLElement>(null)
  const [paragraphSpacingAnchor, setParagraphSpacingAnchor] = useState<null | HTMLElement>(null)
  const [indentAnchor, setIndentAnchor] = useState<null | HTMLElement>(null)

  // Estado para exportação
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null)
  
  // Estado para caracteres especiais
  const [specialCharsAnchor, setSpecialCharsAnchor] = useState<null | HTMLElement>(null)
  
  // Estado para painel de outline
  const [showOutlinePanel, setShowOutlinePanel] = useState(false)
  
  // Estado para modo foco e atalhos
  const [focusMode, setFocusMode] = useState(false)

  const [showShortcuts, setShowShortcuts] = useState(false)
  
  // Estado para snippets
  const [showSnippetsPanel, setShowSnippetsPanel] = useState(false)
  
  // Estado para inserção de data
  const [dateMenuAnchor, setDateMenuAnchor] = useState<null | HTMLElement>(null)
  
  // Estado para marca d'água
  const [showWatermark, setShowWatermark] = useState(false)
  
  // Estado para meta de palavras/páginas
  const [wordTarget, setWordTarget] = useState<number | null>(null)
  const [wordTargetDialog, setWordTargetDialog] = useState(false)
  const [wordTargetInput, setWordTargetInput] = useState('')
  
  // Estado para menu de inserções jurídicas
  const [legalInsertAnchor, setLegalInsertAnchor] = useState<null | HTMLElement>(null)
  
  // Estado para highlight/revisão colorido
  const [highlightMenuAnchor, setHighlightMenuAnchor] = useState<null | HTMLElement>(null)
  
  // Estado para cabeçalho/rodapé personalizado
  const [headerFooterDialog, setHeaderFooterDialog] = useState(false)
  const [customHeader, setCustomHeader] = useState('')
  const [customFooter, setCustomFooter] = useState('Página {page}')
  const [firstPageDifferent, setFirstPageDifferent] = useState(false)
  
  // Estado para diálogo de imagem
  const [imageDialog, setImageDialog] = useState(false)
  const [imageUrl, setImageUrl] = useState('')
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  
  // Estado para edição de imagem
  const [editImageDialog, setEditImageDialog] = useState(false)
  const [selectedImage, setSelectedImage] = useState<{
    src: string;
    width: number;
    height: number;
    originalWidth: number;
    originalHeight: number;
    node: any;
    pos: number;
  } | null>(null)
  const [imageWidth, setImageWidth] = useState<number>(100)
  const [imageHeight, setImageHeight] = useState<number>(100)
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true)
  const [imageAlignment, setImageAlignment] = useState<'left' | 'center' | 'right'>('center')
  
  // Estados para validação de campos
  const [fieldsDialog, setFieldsDialog] = useState(false)
  const [showFieldsPanel, setShowFieldsPanel] = useState(false)
  
  // Estados para reescrita com IA
  const [aiRewriteOpen, setAiRewriteOpen] = useState(false)
  const [aiRewriteSelection, setAiRewriteSelection] = useState<{
    text: string;
    context: string;
    from: number;
    to: number;
  } | null>(null)
  
  // Estado para reescrita completa do documento
  const [aiFullRewriteOpen, setAiFullRewriteOpen] = useState(false)
  const [documentFields, setDocumentFields] = useState<Array<{
    placeholder: string;
    value: string;
    required: boolean;
  }>>([])
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({})

  const [pageSize, setPageSize] = useState<'A4' | 'LETTER' | 'LEGAL'>('A4')
  const [margins, setMargins] = useState({ top: 96, bottom: 72, left: 76, right: 76 })
  const editorRef = useRef<ReturnType<typeof useEditor>>(null)

  // Configurar editor TipTap
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      FontFamily.configure({
        types: ['textStyle'],
      }),
      CharacterCount,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      Link.configure({
        openOnClick: false,
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
      Underline,
      Superscript,
      Subscript,
      Placeholder.configure({
        placeholder: 'Comece a digitar seu documento jurídico...',
        emptyEditorClass: 'is-editor-empty',
      }),
      FontSizeExtension,
      LineSpacingExtension,
      ParagraphSpacingExtension,
      SmartTypographyExtension,
      PlaceholderHighlight,
      SearchAndReplace.configure({
        searchResultClass: 'search-result-highlight',
        disableRegex: true,
      }),
      PaginationPlus.configure({
        pageHeight: 1123,
        pageWidth: 794,
        pageGap: 40,
        pageBreakBackground: '#f0f0f0',
        pageGapBorderColor: '#bdbdbd',
        pageGapBorderSize: 1,
        marginTop: margins.top,
        marginBottom: margins.bottom,
        marginLeft: margins.left,
        marginRight: margins.right,
        contentMarginTop: 10,
        contentMarginBottom: 10,
        headerLeft: '',
        headerRight: '',
        footerLeft: '<span style="font-family: Arial, sans-serif; font-size: 9pt; color: #888; display:block; text-align:center; width:100%;">{page}</span>',
        footerRight: '',
      }),
    ],
    content,
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      const newContent = editor.getHTML()
      // sync imediato p/ o pai (autosave usa hasUnsavedChanges); contagem de
      // placeholders é debounced para não serializar a cada tecla.
      onContentChange(newContent)
      if (updateDebounceRef.current) clearTimeout(updateDebounceRef.current)
      updateDebounceRef.current = setTimeout(() => {
        setPlaceholderCount(countPlaceholders(newContent).length)
      }, 400)
    },
  })

  // Navega até o próximo placeholder [CAMPO] a partir do cursor, selecionando-o
  const goToNextPlaceholder = useCallback(() => {
    if (!editor) return
    const { state } = editor
    const re = /\[[^\]\n]{2,60}\]/g
    const from = state.selection.to
    const hits: Array<{ start: number; end: number }> = []
    state.doc.descendants((node: any, pos: number) => {
      if (!node.isText || !node.text) return
      let m: RegExpExecArray | null
      re.lastIndex = 0
      while ((m = re.exec(node.text)) !== null) {
        hits.push({ start: pos + m.index, end: pos + m.index + m[0].length })
      }
    })
    if (hits.length === 0) return
    const next = hits.find((h) => h.start >= from) || hits[0]
    editor.chain().focus().setTextSelection({ from: next.start, to: next.end }).run()
    const dom = editor.view.domAtPos(next.start)?.node as HTMLElement | undefined
    const el = dom?.nodeType === 3 ? dom.parentElement : (dom as HTMLElement)
    el?.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
  }, [editor])

  // Export DOCX/PDF via backend autenticado (rota correta /ai-documents/...).
  // Alerta se houver placeholders [CAMPO] pendentes antes de exportar.
  const handleExportDocument = useCallback(async (format: 'docx' | 'pdf') => {
    if (!documentId) {
      setNotification({ open: true, message: 'Salve o documento antes de exportar.', severity: 'info' })
      return
    }
    const pendentes = countPlaceholders(editor?.getHTML() || '')
    if (pendentes.length > 0) {
      const ok = window.confirm(
        `O documento tem ${pendentes.length} campo(s) não preenchido(s) (ex.: ${pendentes[0]}).\n\nExportar mesmo assim?`
      )
      if (!ok) return
    }
    try {
      setNotification({ open: true, message: `Gerando ${format.toUpperCase()}...`, severity: 'info' })
      if (onSave) { try { await onSave() } catch {} }
      const endpoint = format === 'pdf'
        ? `/ai-documents/document/${documentId}/download-pdf`
        : `/ai-documents/document/${documentId}/download-docx`
      const response = await api.get(endpoint, { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const a = document.createElement('a')
      a.href = url
      a.setAttribute('download', `documento.${format}`)
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      setNotification({ open: true, message: `${format.toUpperCase()} baixado com sucesso.`, severity: 'success' })
    } catch (e) {
      setNotification({ open: true, message: `Erro ao gerar ${format.toUpperCase()}. Tente novamente.`, severity: 'error' })
    }
  }, [documentId, editor, onSave])

  // Inicializa/atualiza a contagem de placeholders quando o conteúdo carrega
  useEffect(() => {
    setPlaceholderCount(countPlaceholders(content || '').length)
  }, [content])

  const handlePrintDocument = useCallback(() => {
    const html = editor?.getHTML()?.trim()
    if (!html) return

    const iframe = document.createElement('iframe')
    iframe.style.position = 'fixed'
    iframe.style.right = '0'
    iframe.style.bottom = '0'
    iframe.style.width = '0'
    iframe.style.height = '0'
    iframe.style.border = '0'
    iframe.setAttribute('aria-hidden', 'true')
    document.body.appendChild(iframe)

    const printDocument = iframe.contentWindow?.document
    if (!printDocument || !iframe.contentWindow) {
      document.body.removeChild(iframe)
      return
    }

    printDocument.open()
    printDocument.write(`
      <!DOCTYPE html>
      <html lang="pt-BR">
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>Impressão do documento</title>
          <style>
            @page {
              size: A4;
              margin: 20mm 18mm;
            }
            html, body {
              margin: 0;
              padding: 0;
              background: #fff;
              color: #000;
              font-family: "Times New Roman", serif;
              font-size: 12pt;
              line-height: 1.5;
            }
            .print-document {
              width: 100%;
            }
            .print-document * {
              box-sizing: border-box;
              max-width: 100%;
            }
            .print-document img {
              max-width: 100%;
              height: auto;
              page-break-inside: avoid;
            }
            .print-document table {
              width: 100%;
              border-collapse: collapse;
              page-break-inside: auto;
            }
            .print-document th,
            .print-document td {
              border: 1px solid #000;
              padding: 6pt 8pt;
              vertical-align: top;
            }
            .print-document h1,
            .print-document h2,
            .print-document h3,
            .print-document h4 {
              page-break-after: avoid;
            }
            .print-document p,
            .print-document li {
              orphans: 3;
              widows: 3;
            }
            .print-document hr,
            .print-document hr.page-break {
              border: none;
              height: 0;
              margin: 0;
              page-break-after: always;
            }
          </style>
        </head>
        <body>
          <div class="print-document">${html}</div>
        </body>
      </html>
    `)
    printDocument.close()

    const triggerPrint = () => {
      iframe.contentWindow?.focus()
      iframe.contentWindow?.print()
      window.setTimeout(() => {
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe)
        }
      }, 1000)
    }

    iframe.onload = () => window.setTimeout(triggerPrint, 150)
  }, [editor])

  // Sync editor ref
  ;(editorRef as React.MutableRefObject<typeof editor>).current = editor

  // Sync search term with extension
  useEffect(() => {
    if (editor) {
      editor.commands.setSearchTerm(searchTerm)
    }
  }, [searchTerm, editor])

  useEffect(() => {
    if (editor) {
      editor.commands.setReplaceTerm(replaceTerm)
    }
  }, [replaceTerm, editor])

  // Keyboard shortcuts: Ctrl+F, Ctrl+H, Ctrl+P
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault()
        setShowFindReplace(true)
        setShowReplace(false)
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'h') {
        e.preventDefault()
        setShowFindReplace(true)
        setShowReplace(true)
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'p') {
        e.preventDefault()
        handlePrintDocument()
      }
      if (e.key === 'Escape') {
        setFocusMode(false)
        setShowShortcuts(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  const handleMarginChange = useCallback((side: 'left' | 'right' | 'top' | 'bottom', valuePx: number) => {
    setMargins(prev => {
      const next = { ...prev, [side]: valuePx }
      editorRef.current?.chain().focus().updateMargins({
        top: next.top,
        bottom: next.bottom,
        left: next.left,
        right: next.right,
      }).run()
      return next
    })
  }, [])

  // Referência para o container do editor
  const editorContainerRef = useRef<HTMLDivElement>(null)

  // Handler para clique em imagens
  const handleImageClick = useCallback((event: MouseEvent) => {
    const target = event.target as HTMLElement
    if (target.tagName === 'IMG') {
      event.preventDefault()
      event.stopPropagation()
      
      const img = target as HTMLImageElement
      const imgRect = img.getBoundingClientRect()
      
      // Encontrar a posição do nó da imagem no editor
      if (editor) {
        const { state } = editor
        let imagePos = -1
        let imageNode = null
        
        state.doc.descendants((node, pos) => {
          if (node.type.name === 'image' && node.attrs.src === img.src) {
            imagePos = pos
            imageNode = node
            return false // Para a busca
          }
        })
        
        if (imagePos >= 0) {
          setSelectedImage({
            src: img.src,
            width: img.naturalWidth || img.width,
            height: img.naturalHeight || img.height,
            originalWidth: img.naturalWidth || img.width,
            originalHeight: img.naturalHeight || img.height,
            node: imageNode,
            pos: imagePos
          })
          setImageWidth(img.width)
          setImageHeight(img.height)
          setEditImageDialog(true)
        }
      }
    }
  }, [editor])

  // Adicionar listener de clique em imagens
  useEffect(() => {
    const container = editorContainerRef.current
    if (container) {
      container.addEventListener('click', handleImageClick)
      return () => {
        container.removeEventListener('click', handleImageClick)
      }
    }
  }, [handleImageClick])

  // Handler para atualizar dimensões mantendo proporção
  const handleWidthChange = (newWidth: number) => {
    setImageWidth(newWidth)
    if (maintainAspectRatio && selectedImage) {
      const ratio = selectedImage.originalHeight / selectedImage.originalWidth
      setImageHeight(Math.round(newWidth * ratio))
    }
  }

  const handleHeightChange = (newHeight: number) => {
    setImageHeight(newHeight)
    if (maintainAspectRatio && selectedImage) {
      const ratio = selectedImage.originalWidth / selectedImage.originalHeight
      setImageWidth(Math.round(newHeight * ratio))
    }
  }

  // Aplicar alterações na imagem
  const applyImageChanges = () => {
    if (editor && selectedImage) {
      // Atualizar a imagem com as novas dimensões
      editor.chain().focus()
        .setImage({ 
          src: selectedImage.src,
        })
        .run()
      
      // Aplicar estilo inline para dimensões
      setTimeout(() => {
        const images = document.querySelectorAll('.ProseMirror img')
        images.forEach((img) => {
          if ((img as HTMLImageElement).src === selectedImage.src) {
            (img as HTMLElement).style.width = `${imageWidth}px`
            ;(img as HTMLElement).style.height = `${imageHeight}px`
            
            // Aplicar alinhamento
            const parent = img.parentElement
            if (parent) {
              if (imageAlignment === 'left') {
                (img as HTMLElement).style.marginLeft = '0'
                ;(img as HTMLElement).style.marginRight = 'auto'
              } else if (imageAlignment === 'right') {
                (img as HTMLElement).style.marginLeft = 'auto'
                ;(img as HTMLElement).style.marginRight = '0'
              } else {
                (img as HTMLElement).style.marginLeft = 'auto'
                ;(img as HTMLElement).style.marginRight = 'auto'
              }
            }
          }
        })
        
        // Forçar atualização do conteúdo
        onContentChange(editor.getHTML())
      }, 100)
      
      setEditImageDialog(false)
      setSelectedImage(null)
      setNotification({
        open: true,
        message: 'Imagem atualizada com sucesso!',
        severity: 'success'
      })
    }
  }

  // Remover imagem
  const removeImage = () => {
    if (editor && selectedImage) {
      editor.chain().focus().deleteSelection().run()
      setEditImageDialog(false)
      setSelectedImage(null)
      setNotification({
        open: true,
        message: 'Imagem removida!',
        severity: 'info'
      })
    }
  }

  // Detectar campos [] vazios no documento
  const detectEmptyFields = () => {
    if (!editor) return;
    
    const html = editor.getHTML();
    const text = editor.getText();
    
    // Procurar apenas padrões [campo] não preenchidos
    const fieldPattern = /\[([^\]]+)\]/g;
    const foundFields = new Set<string>();
    let match;

    while ((match = fieldPattern.exec(text)) !== null) {
      const fieldName = match[1].trim();
      // Verificar se não é um campo já preenchido (ex: [PREENCHIDO])
      if (fieldName && 
          !fieldName.match(/^\d+$/) && // Não é só número
          !fieldName.includes('PREENCHIDO') && 
          !fieldName.includes('COMPLETO') &&
          fieldName.length > 1) {
        foundFields.add(fieldName);
      }
    }

    const fieldsList: Array<{placeholder: string; value: string; required: boolean}> = 
      Array.from(foundFields).map(fieldName => ({
        placeholder: fieldName,
        value: fieldValues[fieldName] || '',
        required: true
      }));

    setDocumentFields(fieldsList);
    
    if (fieldsList.length > 0) {
      setShowFieldsPanel(true);
      // Inicializar valores vazios se não existirem
      const initialValues: Record<string, string> = {};
      fieldsList.forEach(field => {
        if (!fieldValues[field.placeholder]) {
          initialValues[field.placeholder] = '';
        }
      });
      setFieldValues(prev => ({ ...prev, ...initialValues }));
    } else {
      setNotification({
        open: true,
        message: 'Nenhum campo [texto] pendente encontrado no documento',
        severity: 'success'
      });
    }
  };

  // Aplicar valores dos campos ao documento
  const handleFieldUpdate = (fields: Record<string, string>) => {
    if (!editor) return;
    
    let html = editor.getHTML();
    
    Object.entries(fields).forEach(([placeholder, value]) => {
      if (value.trim()) {
        // Substituir apenas padrões [campo]
        html = html.replace(new RegExp(`\\[${placeholder}\\]`, 'g'), value);
      }
    });
    
    editor.commands.setContent(html);
    
    setNotification({
      open: true,
      message: 'Campos aplicados com sucesso!',
      severity: 'success'
    });

    // Atualizar fieldValues para manter estado
    setFieldValues(prev => ({ ...prev, ...fields }));
  };

  // Inserir jurisprudência no documento (texto)
  const handleInsertJurisprudenceText = (jurisprudenceText: string) => {
    if (!editor) return;
    
    editor.chain().focus().insertContent(jurisprudenceText).run();
    
    setNotification({
      open: true,
      message: 'Jurisprudência inserida com sucesso!',
      severity: 'success'
    });
  };

  // Buscar jurisprudência usando API real
  const searchJurisprudence = useCallback(async (query: string): Promise<JurisprudenceResult[]> => {
    try {
      const response = await api.get('/jurisprudence/search', {
        params: {
          query: query
        }
      });

      if (response.data.status === 'success' && response.data.data) {
        // Converte os dados da API para o formato esperado pelo SimpleLegalEditor
        const convertedResults: JurisprudenceResult[] = response.data.data.map((item: any, index: number) => ({
          id: item.id || `result_${index}`,
          titulo: item.classe || 'Processo Judicial',
          tribunal: item.tribunal || 'Tribunal não informado',
          numero_processo: item.numero_processo || 'Número não informado',
          relator: 'Não informado', // API DataJud não tem essa informação
          data_julgamento: item.data_ajuizamento || 'Data não informada',
          ementa: item.ementa || 'Ementa não disponível',
          inteiro_teor: item.ementa || 'Conteúdo não disponível',
          relevancia: item.relevancia || 0.5
        }));

        return convertedResults;
      } else {
        return [];
      }
    } catch (error) {
      console.error('Erro ao buscar jurisprudência:', error);
      return [];
    }
  }, [])

  // Buscar jurisprudência
  const handleJurisprudenceSearch = async () => {
    if (!jurisprudenceSearch.trim()) return
    
    const results = await searchJurisprudence(jurisprudenceSearch)
    setJurisprudenceResults(results)
  }

  // Inserir jurisprudência selecionada
  const insertJurisprudenceInEditor = (jurisprudence: JurisprudenceResult) => {
    if (editor) {
      const jurisprudenceHtml = `
        <div class="jurisprudence-citation" style="border: 1px solid #e0e0e0; border-radius: 8px; padding: 16px; margin: 16px 0; background-color: #fafafa; position: relative;">
          <h4 style="margin: 0 0 8px 0; color: #1976d2; font-size: 14pt;">${jurisprudence.tribunal} - ${jurisprudence.numero_processo || 'Processo'}</h4>
          <p><strong>Relator:</strong> ${jurisprudence.relator || 'Não informado'}</p>
          <p><strong>Data:</strong> ${jurisprudence.data_julgamento || 'Não informada'}</p>
          <blockquote style="margin: 12px 0; padding: 8px 16px; border-left: 3px solid #1976d2; background-color: white; font-style: italic;">${jurisprudence.ementa}</blockquote>
        </div>
      `
      
      editor.commands.insertContent(jurisprudenceHtml)
      setJurisprudenceDialog(false)
      setJurisprudenceSearch('')
      setJurisprudenceResults([])
      
      setNotification({
        open: true,
        message: 'Jurisprudência inserida com sucesso!',
        severity: 'success'
      })
    }
  }

  const iconBtnSx = { minWidth: 28, p: '5px' }
  const groupSx = { '& .MuiButtonBase-root': iconBtnSx }

  // Toolbar reorganizada em duas linhas compactas
  const renderToolbar = () => (
    <Paper sx={{ borderRadius: 0, borderBottom: 1, borderColor: 'divider', backgroundColor: '#f8f9fa' }} elevation={0}>
      {/* ──── Row 1: Formatação de texto ──── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, pt: 0.75, pb: 0.25, flexWrap: 'wrap' }}>
        {/* Undo / Redo */}
        <ButtonGroup size="small" variant="text" sx={groupSx}>
          <Tooltip title="Desfazer (Ctrl+Z)">
            <span>
              <IconButton onClick={() => editor?.chain().focus().undo().run()} disabled={!editor?.can().undo()} sx={iconBtnSx}><Undo fontSize="small" /></IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Refazer (Ctrl+Y)">
            <span>
              <IconButton onClick={() => editor?.chain().focus().redo().run()} disabled={!editor?.can().redo()} sx={iconBtnSx}><Redo fontSize="small" /></IconButton>
            </span>
          </Tooltip>
        </ButtonGroup>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

        {/* Font + Size */}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select value={currentFontFamily} onChange={(e) => { setCurrentFontFamily(e.target.value); editor?.chain().focus().setFontFamily(e.target.value).run(); }}
            sx={{ '& .MuiSelect-select': { py: 0.4, fontSize: 13 }, height: 30 }}>
            <MenuItem disabled sx={{ fontSize: 11, color: 'text.secondary', minHeight: 'auto', py: 0.5 }}>— Serifadas (formais) —</MenuItem>
            <MenuItem value="Times New Roman" sx={{ fontFamily: 'Times New Roman' }}>Times New Roman</MenuItem>
            <MenuItem value="Georgia" sx={{ fontFamily: 'Georgia' }}>Georgia</MenuItem>
            <MenuItem value="Garamond" sx={{ fontFamily: 'Garamond' }}>Garamond</MenuItem>
            <MenuItem value="Book Antiqua" sx={{ fontFamily: 'Book Antiqua' }}>Book Antiqua</MenuItem>
            <MenuItem value="Palatino" sx={{ fontFamily: 'Palatino' }}>Palatino Linotype</MenuItem>
            <MenuItem value="Cambria" sx={{ fontFamily: 'Cambria' }}>Cambria</MenuItem>
            <MenuItem value="Baskerville" sx={{ fontFamily: 'Baskerville' }}>Baskerville</MenuItem>
            <MenuItem value="Century Schoolbook" sx={{ fontFamily: 'Century Schoolbook' }}>Century Schoolbook</MenuItem>
            <MenuItem value="Bookman Old Style" sx={{ fontFamily: 'Bookman Old Style' }}>Bookman Old Style</MenuItem>
            <MenuItem disabled sx={{ fontSize: 11, color: 'text.secondary', minHeight: 'auto', py: 0.5 }}>— Sem serifa (modernas) —</MenuItem>
            <MenuItem value="Arial" sx={{ fontFamily: 'Arial' }}>Arial</MenuItem>
            <MenuItem value="Calibri" sx={{ fontFamily: 'Calibri' }}>Calibri</MenuItem>
            <MenuItem value="Helvetica" sx={{ fontFamily: 'Helvetica' }}>Helvetica</MenuItem>
            <MenuItem value="Verdana" sx={{ fontFamily: 'Verdana' }}>Verdana</MenuItem>
            <MenuItem value="Tahoma" sx={{ fontFamily: 'Tahoma' }}>Tahoma</MenuItem>
            <MenuItem value="Trebuchet MS" sx={{ fontFamily: 'Trebuchet MS' }}>Trebuchet MS</MenuItem>
            <MenuItem value="Segoe UI" sx={{ fontFamily: 'Segoe UI' }}>Segoe UI</MenuItem>
            <MenuItem disabled sx={{ fontSize: 11, color: 'text.secondary', minHeight: 'auto', py: 0.5 }}>— Monoespaçadas —</MenuItem>
            <MenuItem value="Courier New" sx={{ fontFamily: 'Courier New' }}>Courier New</MenuItem>
            <MenuItem value="Consolas" sx={{ fontFamily: 'Consolas' }}>Consolas</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 58 }}>
          <Select value={currentFontSize} onChange={(e) => { const s = e.target.value; setCurrentFontSize(s); (editor as any)?.chain().focus().setFontSize(s).run(); }}
            sx={{ '& .MuiSelect-select': { py: 0.4, fontSize: 13 }, height: 30 }}>
            {['8pt','9pt','10pt','11pt','12pt','14pt','16pt','18pt','20pt','24pt','28pt','36pt'].map(s => (
              <MenuItem key={s} value={s}>{s.replace('pt','')}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

        {/* B I U S */}
        <ButtonGroup size="small" variant="text" sx={groupSx}>
          <Tooltip title="Negrito (Ctrl+B)">
            <IconButton onClick={() => editor?.chain().focus().toggleBold().run()} color={editor?.isActive('bold') ? 'primary' : 'default'} sx={iconBtnSx}><FormatBold fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Itálico (Ctrl+I)">
            <IconButton onClick={() => editor?.chain().focus().toggleItalic().run()} color={editor?.isActive('italic') ? 'primary' : 'default'} sx={iconBtnSx}><FormatItalic fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Sublinhado (Ctrl+U)">
            <IconButton onClick={() => editor?.chain().focus().toggleUnderline().run()} color={editor?.isActive('underline') ? 'primary' : 'default'} sx={iconBtnSx}><FormatUnderlined fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Tachado">
            <IconButton onClick={() => editor?.chain().focus().toggleStrike().run()} color={editor?.isActive('strike') ? 'primary' : 'default'} sx={iconBtnSx}><StrikethroughS fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Sobrescrito (Art. 5º)">
            <IconButton onClick={() => editor?.chain().focus().toggleSuperscript().run()} color={editor?.isActive('superscript') ? 'primary' : 'default'} sx={iconBtnSx}><SuperscriptIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Subscrito">
            <IconButton onClick={() => editor?.chain().focus().toggleSubscript().run()} color={editor?.isActive('subscript') ? 'primary' : 'default'} sx={iconBtnSx}><SubscriptIcon fontSize="small" /></IconButton>
          </Tooltip>
        </ButtonGroup>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

        {/* Text Color + Highlight */}
        <ButtonGroup size="small" variant="text" sx={groupSx}>
          <Tooltip title="Cor do texto">
            <IconButton onClick={(e) => setColorMenuAnchor(e.currentTarget)} sx={iconBtnSx}><FormatColorText fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Marcadores de revisão (clique para amarelo, segure para mais cores)">
            <IconButton
              onClick={() => editor?.chain().focus().toggleHighlight({ color: '#fef08a' }).run()}
              onContextMenu={(e) => { e.preventDefault(); setHighlightMenuAnchor(e.currentTarget); }}
              color={editor?.isActive('highlight') ? 'primary' : 'default'}
              sx={iconBtnSx}
            >
              <HighlightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </ButtonGroup>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

        {/* Alignment */}
        <ButtonGroup size="small" variant="text" sx={groupSx}>
          <Tooltip title="Esquerda">
            <IconButton onClick={() => editor?.chain().focus().setTextAlign('left').run()} color={editor?.isActive({ textAlign: 'left' }) ? 'primary' : 'default'} sx={iconBtnSx}><FormatAlignLeft fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Centro">
            <IconButton onClick={() => editor?.chain().focus().setTextAlign('center').run()} color={editor?.isActive({ textAlign: 'center' }) ? 'primary' : 'default'} sx={iconBtnSx}><FormatAlignCenter fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Direita">
            <IconButton onClick={() => editor?.chain().focus().setTextAlign('right').run()} color={editor?.isActive({ textAlign: 'right' }) ? 'primary' : 'default'} sx={iconBtnSx}><FormatAlignRight fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Justificar">
            <IconButton onClick={() => editor?.chain().focus().setTextAlign('justify').run()} color={editor?.isActive({ textAlign: 'justify' }) ? 'primary' : 'default'} sx={iconBtnSx}><FormatAlignJustify fontSize="small" /></IconButton>
          </Tooltip>
        </ButtonGroup>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

        {/* Line / Paragraph Spacing + Indent */}
        <ButtonGroup size="small" variant="text" sx={groupSx}>
          <Tooltip title="Espaçamento entre linhas">
            <IconButton onClick={(e) => setLineSpacingAnchor(e.currentTarget)} sx={iconBtnSx}><FormatLineSpacing fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Espaçamento entre parágrafos">
            <IconButton onClick={(e) => setParagraphSpacingAnchor(e.currentTarget)} sx={iconBtnSx}><ParagraphSpacingIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Recuo primeira linha (ABNT)">
            <IconButton onClick={(e) => setIndentAnchor(e.currentTarget)} sx={iconBtnSx}><IndentIcon fontSize="small" /></IconButton>
          </Tooltip>
        </ButtonGroup>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

        {/* Clear + Find */}
        <ButtonGroup size="small" variant="text" sx={groupSx}>
          <Tooltip title="Limpar formatação">
            <IconButton onClick={() => editor?.chain().focus().clearNodes().unsetAllMarks().run()} sx={iconBtnSx}><FormatClear fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Buscar e substituir (Ctrl+F)">
            <IconButton onClick={() => { setShowFindReplace(true); setShowReplace(false); }} color={showFindReplace ? 'primary' : 'default'} sx={iconBtnSx}><FindReplace fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Caracteres especiais (§, º, ª, ©)">
            <IconButton onClick={(e) => setSpecialCharsAnchor(e.currentTarget)} color={Boolean(specialCharsAnchor) ? 'primary' : 'default'} sx={iconBtnSx}><SpecialCharsIcon fontSize="small" /></IconButton>
          </Tooltip>
        </ButtonGroup>
      </Box>

      {/* ──── Row 2: Estrutura + Inserir + Ações ──── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, pt: 0.25, pb: 0.75, flexWrap: 'wrap' }}>
        {/* Paragraph Style Dropdown */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={
              editor?.isActive('heading', { level: 1 }) ? 'h1' :
              editor?.isActive('heading', { level: 2 }) ? 'h2' :
              editor?.isActive('heading', { level: 3 }) ? 'h3' :
              editor?.isActive('heading', { level: 4 }) ? 'h4' : 'p'
            }
            onChange={(e) => {
              const v = e.target.value
              if (v === 'p') editor?.chain().focus().setParagraph().run()
              else if (v === 'h1') editor?.chain().focus().toggleHeading({ level: 1 }).run()
              else if (v === 'h2') editor?.chain().focus().toggleHeading({ level: 2 }).run()
              else if (v === 'h3') editor?.chain().focus().toggleHeading({ level: 3 }).run()
              else if (v === 'h4') editor?.chain().focus().toggleHeading({ level: 4 }).run()
            }}
            sx={{ '& .MuiSelect-select': { py: 0.4, fontSize: 13 }, height: 30 }}
          >
            <MenuItem value="p"><Typography sx={{ fontSize: 13 }}>Texto normal</Typography></MenuItem>
            <MenuItem value="h1"><Typography sx={{ fontSize: 18, fontWeight: 700 }}>Título 1</Typography></MenuItem>
            <MenuItem value="h2"><Typography sx={{ fontSize: 16, fontWeight: 700 }}>Título 2</Typography></MenuItem>
            <MenuItem value="h3"><Typography sx={{ fontSize: 14, fontWeight: 700 }}>Título 3</Typography></MenuItem>
            <MenuItem value="h4"><Typography sx={{ fontSize: 13, fontWeight: 600, fontStyle: 'italic' }}>Título 4</Typography></MenuItem>
          </Select>
        </FormControl>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

        {/* Lists + Indent */}
        <ButtonGroup size="small" variant="text" sx={groupSx}>
          <Tooltip title="Lista com marcadores">
            <IconButton onClick={() => editor?.chain().focus().toggleBulletList().run()} color={editor?.isActive('bulletList') ? 'primary' : 'default'} sx={iconBtnSx}><FormatListBulleted fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Lista numerada">
            <IconButton onClick={() => editor?.chain().focus().toggleOrderedList().run()} color={editor?.isActive('orderedList') ? 'primary' : 'default'} sx={iconBtnSx}><FormatListNumbered fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Aumentar indentação">
            <IconButton onClick={() => editor?.chain().focus().sinkListItem('listItem').run()} sx={iconBtnSx}><FormatIndentIncrease fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Diminuir indentação">
            <IconButton onClick={() => editor?.chain().focus().liftListItem('listItem').run()} sx={iconBtnSx}><FormatIndentDecrease fontSize="small" /></IconButton>
          </Tooltip>
        </ButtonGroup>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

        {/* Insert: Link, Image, Table, Quote, Code, Page Break */}
        <ButtonGroup size="small" variant="text" sx={groupSx}>
          <Tooltip title="Inserir link">
            <IconButton onClick={() => { const url = prompt('Digite a URL:'); if (url) editor?.chain().focus().setLink({ href: url }).run(); }} sx={iconBtnSx}><LinkIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Inserir imagem">
            <IconButton onClick={() => setImageDialog(true)} sx={iconBtnSx}><ImageIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Tabela">
            <IconButton onClick={() => editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} sx={iconBtnSx}><TableIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Citação">
            <IconButton onClick={() => editor?.chain().focus().toggleBlockquote().run()} color={editor?.isActive('blockquote') ? 'primary' : 'default'} sx={iconBtnSx}><FormatQuote fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Quebra de página">
            <IconButton onClick={() => editor?.chain().focus().setHorizontalRule().run()} sx={iconBtnSx}><InsertPageBreak fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Inserir índice / sumário">
            <IconButton onClick={() => {
              if (!editor) return
              const headings: { level: number; text: string }[] = []
              editor.state.doc.descendants((node) => {
                if (node.type.name === 'heading' && node.textContent.trim()) {
                  headings.push({ level: node.attrs.level, text: node.textContent.trim() })
                }
              })
              if (headings.length === 0) {
                setNotification({ open: true, message: 'Nenhum título encontrado no documento. Adicione títulos (H1, H2, H3) primeiro.', severity: 'info' })
                return
              }
              const tocHtml = `<div style="border:1px solid #e0e0e0;border-radius:6px;padding:16px 20px;margin:16px 0;background:#fafafa;">` +
                `<p style="font-weight:700;font-size:14pt;margin:0 0 10px 0;color:#1a365d;">SUMÁRIO</p>` +
                headings.map(h => {
                  const indent = (h.level - 1) * 20
                  const weight = h.level === 1 ? '700' : h.level === 2 ? '600' : '400'
                  const size = h.level === 1 ? '12pt' : '11pt'
                  return `<p style="margin:3px 0;padding-left:${indent}px;font-weight:${weight};font-size:${size};line-height:1.6;">${h.text}</p>`
                }).join('') +
                `</div>`
              editor.chain().focus().insertContent(tocHtml).run()
              setNotification({ open: true, message: `Sumário inserido com ${headings.length} itens.`, severity: 'success' })
            }} sx={iconBtnSx}><TocIcon fontSize="small" /></IconButton>
          </Tooltip>
        </ButtonGroup>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

        {/* Jurídico: Jurisprudência + Campos + Outline + Snippets */}
        <ButtonGroup size="small" variant="text" sx={groupSx}>
          <Tooltip title="Jurisprudência">
            <IconButton onClick={() => setJurisprudenceDialog(!jurisprudenceDialog)} color={jurisprudenceDialog ? 'primary' : 'default'} sx={iconBtnSx}><Gavel fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Campos do documento">
            <IconButton onClick={() => setShowFieldsPanel(!showFieldsPanel)} color={showFieldsPanel ? 'primary' : 'default'} sx={iconBtnSx}><Template fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Navegação do documento">
            <IconButton onClick={() => setShowOutlinePanel(!showOutlinePanel)} color={showOutlinePanel ? 'primary' : 'default'} sx={iconBtnSx}><OutlineIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Frases jurídicas">
            <IconButton onClick={() => setShowSnippetsPanel(!showSnippetsPanel)} color={showSnippetsPanel ? 'primary' : 'default'} sx={iconBtnSx}><SnippetsIcon fontSize="small" /></IconButton>
          </Tooltip>
        </ButtonGroup>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

        {/* Legal Tools: Numbering, Signature, Date, Money, Watermark */}
        <ButtonGroup size="small" variant="text" sx={groupSx}>
          <Tooltip title="Inserções jurídicas (numeração, assinatura, data)">
            <IconButton onClick={(e) => setLegalInsertAnchor(e.currentTarget)} sx={iconBtnSx}><LegalNumberIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Valor por extenso (R$)">
            <IconButton onClick={() => {
              if (!editor) return
              const { from, to } = editor.state.selection
              if (from === to) {
                setNotification({ open: true, message: 'Selecione um valor monetário (ex: R$ 1.500,00) para converter por extenso.', severity: 'info' })
                return
              }
              const selectedText = editor.state.doc.textBetween(from, to, ' ')
              const valor = parseMonetaryValue(selectedText)
              if (valor === null || valor < 0) {
                setNotification({ open: true, message: 'Texto selecionado não é um valor monetário válido.', severity: 'error' })
                return
              }
              const extenso = valorPorExtenso(valor)
              const formatted = selectedText.trim().replace(/\s+/g, '') 
              editor.chain().focus().deleteRange({ from, to }).insertContent(`${selectedText.trim()} (${extenso})`).run()
              setNotification({ open: true, message: 'Valor por extenso inserido!', severity: 'success' })
            }} sx={iconBtnSx}><MoneyIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title={showWatermark ? 'Remover marca d\'água MINUTA' : 'Marca d\'água MINUTA'}>
            <IconButton onClick={() => setShowWatermark(!showWatermark)} color={showWatermark ? 'primary' : 'default'} sx={iconBtnSx}><WatermarkIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Meta de palavras">
            <IconButton onClick={() => { setWordTargetInput(wordTarget?.toString() || ''); setWordTargetDialog(true); }} color={wordTarget ? 'primary' : 'default'} sx={iconBtnSx}><TargetIcon fontSize="small" /></IconButton>
          </Tooltip>
        </ButtonGroup>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

        {/* Actions: AI, Save, Export, Print */}
        <ButtonGroup size="small" variant="text" sx={groupSx}>
          <Tooltip title="Reescrever com IA">
            <IconButton onClick={() => setAiFullRewriteOpen(true)} sx={{ ...iconBtnSx, color: '#1a365d' }}><AIIcon fontSize="small" /></IconButton>
          </Tooltip>
          {onSave && (
            <Tooltip title="Salvar (Ctrl+S)">
              <IconButton onClick={onSave} sx={iconBtnSx}><Save fontSize="small" /></IconButton>
            </Tooltip>
          )}
          <Tooltip title="Exportar">
            <IconButton onClick={(e) => setExportMenuAnchor(e.currentTarget)} sx={iconBtnSx}><FileDownload fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Imprimir (Ctrl+P)">
            <IconButton onClick={handlePrintDocument} sx={iconBtnSx}><PrintOutlined fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title={focusMode ? 'Sair do modo foco (Esc)' : 'Modo foco'}>
            <IconButton onClick={() => setFocusMode(!focusMode)} color={focusMode ? 'primary' : 'default'} sx={iconBtnSx}><FocusModeIcon fontSize="small" /></IconButton>
          </Tooltip>
        </ButtonGroup>

        {/* ── Right-aligned: Page size + Zoom ── */}
        {editor && (
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small" variant="standard" sx={{ minWidth: 60 }}>
              <Select value={pageSize} onChange={(e) => { const s = e.target.value as 'A4'|'LETTER'|'LEGAL'; setPageSize(s); editor.chain().focus().updatePageSize(PAGE_SIZES[s]).run(); }}
                sx={{ fontSize: '0.7rem', '& .MuiSelect-select': { py: 0.2, display: 'flex', alignItems: 'center', gap: 0.5 } }}
                startAdornment={<DescriptionIcon sx={{ fontSize: 13, color: 'text.secondary', mr: 0.3 }} />}>
                <MenuItem value="A4" sx={{ fontSize: '0.75rem' }}>A4</MenuItem>
                <MenuItem value="LETTER" sx={{ fontSize: '0.75rem' }}>Carta</MenuItem>
                <MenuItem value="LEGAL" sx={{ fontSize: '0.75rem' }}>Ofício</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="Cabeçalho e rodapé">
              <IconButton size="small" onClick={() => setHeaderFooterDialog(true)} sx={{ p: '3px' }}><DescriptionIcon sx={{ fontSize: 14, color: 'text.secondary' }} /></IconButton>
            </Tooltip>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <IconButton size="small" onClick={() => setZoom(p => Math.max(50, p - 10))} disabled={zoom <= 50} sx={{ p: '3px' }}><ZoomOut sx={{ fontSize: 16 }} /></IconButton>
              <Chip label={`${zoom}%`} size="small" onClick={() => setZoom(100)} sx={{ minWidth: 48, cursor: 'pointer', fontSize: '0.7rem', height: 22, '&:hover': { backgroundColor: 'action.hover' } }} />
              <IconButton size="small" onClick={() => setZoom(p => Math.min(200, p + 10))} disabled={zoom >= 200} sx={{ p: '3px' }}><ZoomIn sx={{ fontSize: 16 }} /></IconButton>
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  )

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {!focusMode && renderToolbar()}

      {/* Find & Replace Panel */}
      {showFindReplace && !focusMode && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
          backgroundColor: '#f8f9fa', borderBottom: '1px solid #e0e0e0',
          flexShrink: 0,
        }}>
          <input
            autoFocus
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar..."
            style={{
              border: '1px solid #ccc', borderRadius: 4, padding: '4px 8px',
              fontSize: 14, minWidth: 180, outline: 'none',
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') editor?.commands.nextSearchResult()
              if (e.key === 'Escape') { setShowFindReplace(false); setSearchTerm(''); }
            }}
          />
          <Tooltip title="Anterior">
            <IconButton size="small" onClick={() => editor?.commands.previousSearchResult()}>
              <NavigateBefore fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Próximo (Enter)">
            <IconButton size="small" onClick={() => editor?.commands.nextSearchResult()}>
              <NavigateNext fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={showReplace ? 'Ocultar substituir' : 'Substituir (Ctrl+H)'}>
            <IconButton size="small" onClick={() => setShowReplace(!showReplace)} color={showReplace ? 'primary' : 'default'}>
              <SwapHoriz fontSize="small" />
            </IconButton>
          </Tooltip>
          {showReplace && (
            <>
              <input
                value={replaceTerm}
                onChange={(e) => setReplaceTerm(e.target.value)}
                placeholder="Substituir por..."
                style={{
                  border: '1px solid #ccc', borderRadius: 4, padding: '4px 8px',
                  fontSize: 14, minWidth: 180, outline: 'none',
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') editor?.commands.replace()
                }}
              />
              <Tooltip title="Substituir atual">
                <Button size="small" variant="outlined" sx={{ minWidth: 'auto', textTransform: 'none', fontSize: 12 }}
                  onClick={() => editor?.commands.replace()}>
                  Substituir
                </Button>
              </Tooltip>
              <Tooltip title="Substituir todos">
                <Button size="small" variant="outlined" sx={{ minWidth: 'auto', textTransform: 'none', fontSize: 12 }}
                  onClick={() => editor?.commands.replaceAll()}>
                  Todos
                </Button>
              </Tooltip>
            </>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {(editor?.storage.searchAndReplace as any)?.results?.length ?? 0} resultados
          </Typography>
          <IconButton size="small" onClick={() => { setShowFindReplace(false); setSearchTerm(''); setReplaceTerm(''); }} sx={{ ml: 'auto' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Line Spacing Menu */}
      <Menu anchorEl={lineSpacingAnchor} open={Boolean(lineSpacingAnchor)} onClose={() => setLineSpacingAnchor(null)}>
        {[
          { label: 'Simples (1.0)', value: '1' },
          { label: '1.15', value: '1.15' },
          { label: '1.5 (Jurídico)', value: '1.5' },
          { label: 'Duplo (2.0)', value: '2' },
        ].map(opt => (
          <MenuItem key={opt.value} onClick={() => {
            (editor as any)?.chain().focus().setLineSpacing(opt.value).run();
            setLineSpacingAnchor(null);
          }}>
            <Typography variant="body2">{opt.label}</Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* Paragraph Spacing Menu */}
      <Menu anchorEl={paragraphSpacingAnchor} open={Boolean(paragraphSpacingAnchor)} onClose={() => setParagraphSpacingAnchor(null)}>
        {[
          { label: 'Nenhum', value: '0pt' },
          { label: 'Pequeno (6pt)', value: '6pt' },
          { label: 'Normal (12pt)', value: '12pt' },
          { label: 'Grande (18pt)', value: '18pt' },
        ].map(opt => (
          <MenuItem key={opt.value} onClick={() => {
            (editor as any)?.chain().focus().setParagraphSpacing(opt.value).run();
            setParagraphSpacingAnchor(null);
          }}>
            <Typography variant="body2">{opt.label}</Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* First-Line Indent Menu */}
      <Menu anchorEl={indentAnchor} open={Boolean(indentAnchor)} onClose={() => setIndentAnchor(null)}>
        {[
          { label: 'Sem recuo', value: '0' },
          { label: '1,25 cm (ABNT)', value: '1.25cm' },
          { label: '2,5 cm', value: '2.5cm' },
          { label: '3,0 cm', value: '3cm' },
        ].map(opt => (
          <MenuItem key={opt.value} onClick={() => {
            (editor as any)?.chain().focus().setTextIndent(opt.value).run();
            setIndentAnchor(null);
          }}>
            <Typography variant="body2">{opt.label}</Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* Export Menu */}
      <Menu anchorEl={exportMenuAnchor} open={Boolean(exportMenuAnchor)} onClose={() => setExportMenuAnchor(null)}>
        <MenuItem onClick={() => {
          setExportMenuAnchor(null);
          handleExportDocument('docx');
        }}>
          <Typography variant="body2">Baixar DOCX</Typography>
        </MenuItem>
        <MenuItem onClick={() => {
          setExportMenuAnchor(null);
          handleExportDocument('pdf');
        }}>
          <Typography variant="body2">Baixar PDF</Typography>
        </MenuItem>
        <MenuItem onClick={() => {
          setExportMenuAnchor(null);
          handlePrintDocument();
        }}>
          <Typography variant="body2">Imprimir / Salvar como PDF (navegador)</Typography>
        </MenuItem>
        <MenuItem onClick={() => {
          setExportMenuAnchor(null);
          const blob = new Blob([editor?.getHTML() || ''], { type: 'text/html' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a'); a.href = url; a.download = 'documento.html'; a.click();
          URL.revokeObjectURL(url);
        }}>
          <Typography variant="body2">Baixar HTML</Typography>
        </MenuItem>
      </Menu>

      {/* Special Characters Popover */}
      <Menu
        anchorEl={specialCharsAnchor}
        open={Boolean(specialCharsAnchor)}
        onClose={() => setSpecialCharsAnchor(null)}
        PaperProps={{ sx: { maxWidth: 340, p: 1 } }}
      >
        <Typography variant="caption" sx={{ px: 1, pb: 0.5, color: 'text.secondary', fontWeight: 600, display: 'block' }}>
          Jurídico
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, px: 0.5, pb: 1 }}>
          {[
            { char: '§', label: 'Parágrafo' }, { char: '°', label: 'Grau' },
            { char: 'º', label: 'Ordinal masc.' }, { char: 'ª', label: 'Ordinal fem.' },
            { char: '†', label: 'Cruz' }, { char: '‡', label: 'Cruz dupla' },
            { char: 'Art.', label: 'Artigo' }, { char: 'Inc.', label: 'Inciso' },
          ].map(({ char, label }) => (
            <Tooltip key={char} title={label} arrow>
              <Button
                size="small"
                variant="outlined"
                onClick={() => { editor?.chain().focus().insertContent(char).run(); setSpecialCharsAnchor(null); }}
                sx={{ minWidth: 36, height: 32, fontSize: char.length > 2 ? 11 : 16, fontWeight: 500, textTransform: 'none', borderColor: '#e0e0e0', color: '#1a365d' }}
              >
                {char}
              </Button>
            </Tooltip>
          ))}
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <Typography variant="caption" sx={{ px: 1, pb: 0.5, color: 'text.secondary', fontWeight: 600, display: 'block' }}>
          Pontuação e símbolos
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, px: 0.5, pb: 1 }}>
          {[
            { char: '—', label: 'Travessão (em dash)' }, { char: '–', label: 'Meia-risca (en dash)' },
            { char: '"', label: 'Aspas esq.' }, { char: '"', label: 'Aspas dir.' },
            { char: '«', label: 'Aspas francesas esq.' }, { char: '»', label: 'Aspas francesas dir.' },
            { char: '…', label: 'Reticências' }, { char: '•', label: 'Bullet' },
            { char: '©', label: 'Copyright' }, { char: '®', label: 'Registrado' },
            { char: '™', label: 'Trademark' }, { char: '½', label: 'Meio' },
            { char: '¼', label: 'Um quarto' }, { char: '¾', label: 'Três quartos' },
            { char: '×', label: 'Multiplicação' }, { char: '÷', label: 'Divisão' },
          ].map(({ char, label }) => (
            <Tooltip key={char + label} title={label} arrow>
              <Button
                size="small"
                variant="outlined"
                onClick={() => { editor?.chain().focus().insertContent(char).run(); setSpecialCharsAnchor(null); }}
                sx={{ minWidth: 36, height: 32, fontSize: 16, fontWeight: 500, textTransform: 'none', borderColor: '#e0e0e0' }}
              >
                {char}
              </Button>
            </Tooltip>
          ))}
        </Box>
      </Menu>

      {/* Highlight / Revision Color Menu */}
      <Menu anchorEl={highlightMenuAnchor} open={Boolean(highlightMenuAnchor)} onClose={() => setHighlightMenuAnchor(null)}
        PaperProps={{ sx: { p: 1, minWidth: 200 } }}>
        <Typography variant="caption" sx={{ px: 1, pb: 0.5, fontWeight: 600, color: 'text.secondary', display: 'block' }}>
          Marcadores de revisão
        </Typography>
        {[
          { color: '#fef08a', label: 'Revisar', emoji: '📝' },
          { color: '#bbf7d0', label: 'Aprovado', emoji: '✅' },
          { color: '#fecaca', label: 'Remover', emoji: '❌' },
          { color: '#bfdbfe', label: 'Verificar legislação', emoji: '⚖️' },
          { color: '#e9d5ff', label: 'Adicionar jurisprudência', emoji: '📚' },
          { color: '#fed7aa', label: 'Atenção', emoji: '⚠️' },
        ].map(({ color, label, emoji }) => (
          <MenuItem key={color} onClick={() => {
            editor?.chain().focus().toggleHighlight({ color }).run()
            setHighlightMenuAnchor(null)
          }} sx={{ gap: 1, fontSize: 13 }}>
            <Box sx={{ width: 18, height: 18, bgcolor: color, borderRadius: '3px', border: '1px solid rgba(0,0,0,0.1)', flexShrink: 0 }} />
            {label}
          </MenuItem>
        ))}
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => {
          editor?.chain().focus().unsetHighlight().run()
          setHighlightMenuAnchor(null)
        }} sx={{ fontSize: 13 }}>
          <Box sx={{ width: 18, height: 18, border: '1px dashed #ccc', borderRadius: '3px', flexShrink: 0, mr: 1 }} />
          Remover destaque
        </MenuItem>
      </Menu>

      {/* Legal Insert Menu */}
      <Menu anchorEl={legalInsertAnchor} open={Boolean(legalInsertAnchor)} onClose={() => setLegalInsertAnchor(null)}
        PaperProps={{ sx: { minWidth: 240 } }}>
        <Typography variant="caption" sx={{ px: 2, py: 0.5, color: 'text.secondary', fontWeight: 600, display: 'block' }}>
          Inserções jurídicas
        </Typography>
        <Divider sx={{ mb: 0.5 }} />
        <MenuItem onClick={() => {
          setLegalInsertAnchor(null)
          if (!editor) return
          const numbering = `<p><strong>Art. 1º</strong> - </p><p style="text-indent:1.25cm">§ 1º - </p><p style="text-indent:1.25cm">§ 2º - </p><p style="text-indent:2.5cm">I - </p><p style="text-indent:2.5cm">II - </p><p style="text-indent:3.75cm">a) </p><p style="text-indent:3.75cm">b) </p>`
          editor.chain().focus().insertContent(numbering).run()
        }}>
          <ListItemIcon><LegalNumberIcon fontSize="small" /></ListItemIcon>
          <Typography variant="body2">Numeração jurídica (Art/§/I/a)</Typography>
        </MenuItem>
        <MenuItem onClick={() => {
          setLegalInsertAnchor(null)
          if (!editor) return
          const signature = `<p style="text-align:center;margin-top:60px;">________________________________</p><p style="text-align:center;"><strong>[Nome do Advogado]</strong></p><p style="text-align:center;">OAB/__ nº _____</p>`
          editor.chain().focus().insertContent(signature).run()
        }}>
          <ListItemIcon><SignatureIcon fontSize="small" /></ListItemIcon>
          <Typography variant="body2">Bloco de assinatura</Typography>
        </MenuItem>
        <MenuItem onClick={(e) => {
          setLegalInsertAnchor(null)
          setDateMenuAnchor(e.currentTarget)
        }}>
          <ListItemIcon><DateIcon fontSize="small" /></ListItemIcon>
          <Typography variant="body2">Inserir data</Typography>
        </MenuItem>
      </Menu>

      {/* Date Insert Menu */}
      <Menu anchorEl={dateMenuAnchor} open={Boolean(dateMenuAnchor)} onClose={() => setDateMenuAnchor(null)}
        PaperProps={{ sx: { minWidth: 280 } }}>
        <Typography variant="caption" sx={{ px: 2, py: 0.5, color: 'text.secondary', fontWeight: 600, display: 'block' }}>
          Inserir data
        </Typography>
        <Divider sx={{ mb: 0.5 }} />
        {(() => {
          const now = new Date()
          const meses = ['janeiro','fevereiro','março','abril','maio','junho','julho','agosto','setembro','outubro','novembro','dezembro']
          const dia = now.getDate()
          const mes = meses[now.getMonth()]
          const ano = now.getFullYear()
          const numDate = `${String(dia).padStart(2,'0')}/${String(now.getMonth()+1).padStart(2,'0')}/${ano}`
          return [
            { label: `São Paulo, ${dia} de ${mes} de ${ano}`, value: `São Paulo, ${dia} de ${mes} de ${ano}` },
            { label: `___, ${dia} de ${mes} de ${ano}`, value: `___, ${dia} de ${mes} de ${ano}` },
            { label: numDate, value: numDate },
            { label: `${dia} de ${mes} de ${ano}`, value: `${dia} de ${mes} de ${ano}` },
          ].map(opt => (
            <MenuItem key={opt.label} onClick={() => {
              editor?.chain().focus().insertContent(opt.value).run()
              setDateMenuAnchor(null)
            }}>
              <Typography variant="body2" sx={{ fontSize: 13 }}>{opt.label}</Typography>
            </MenuItem>
          ))
        })()}
      </Menu>

      {/* Word Target Dialog */}
      <Dialog open={wordTargetDialog} onClose={() => setWordTargetDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: 15 }}>Meta de palavras</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: 12 }}>
            Defina uma meta de palavras para acompanhar o progresso. Útil para peças com limite de páginas.
          </Typography>
          <TextField
            fullWidth size="small" type="number"
            label="Quantidade de palavras"
            value={wordTargetInput}
            onChange={(e) => setWordTargetInput(e.target.value)}
            placeholder="Ex: 3000"
            inputProps={{ min: 0 }}
          />
        </DialogContent>
        <DialogActions>
          {wordTarget && (
            <Button onClick={() => { setWordTarget(null); setWordTargetDialog(false); }} color="inherit" sx={{ mr: 'auto' }}>
              Remover meta
            </Button>
          )}
          <Button onClick={() => setWordTargetDialog(false)} color="inherit">Cancelar</Button>
          <Button variant="contained" onClick={() => {
            const v = parseInt(wordTargetInput)
            setWordTarget(v > 0 ? v : null)
            setWordTargetDialog(false)
          }}>Definir</Button>
        </DialogActions>
      </Dialog>

      {/* Régua horizontal */}
      {!focusMode && (
        <Box sx={{ 
          backgroundColor: '#f8f9fa', 
          borderBottom: '1px solid #e0e0e0', 
          flexShrink: 0,
          minHeight: '24px',
          display: 'flex',
          alignItems: 'stretch',
        }}>
          <Box sx={{ width: `${RULER_WIDTH}px`, flexShrink: 0, backgroundColor: '#f8f9fa', borderRight: '1px solid #e0e0e0' }} />
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <Ruler
              pageWidthPx={pageSize === 'LETTER' || pageSize === 'LEGAL' ? 818 : 794}
              marginLeftPx={margins.left}
              marginRightPx={margins.right}
              onMarginChange={handleMarginChange}
            />
          </Box>
        </Box>
      )}
      
      {/* Area principal: regua vertical + editor */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Régua vertical */}
        {!focusMode && (
          <Box sx={{ 
            flexShrink: 0, 
            width: `${RULER_WIDTH}px`,
            backgroundColor: '#f8f9fa',
            borderRight: '1px solid #e0e0e0',
            overflowY: 'hidden',
            overflowX: 'hidden',
          }}>
            <VerticalRuler
              pageHeightPx={pageSize === 'LEGAL' ? 1404 : pageSize === 'LETTER' ? 1060 : 1123}
              marginTopPx={margins.top}
              marginBottomPx={margins.bottom}
              onMarginChange={handleMarginChange}
            />
          </Box>
        )}

        {/* Editor content */}
        <Box className="editor-pages-wrapper" sx={{ 
          flex: 1, 
          position: 'relative', 
          overflow: 'auto',
          backgroundColor: '#f0f0f0',
          py: 2,
          px: 3,
          minWidth: 0,
        }}>
          {showWatermark && (
            <Box sx={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              pointerEvents: 'none', zIndex: 10, overflow: 'hidden',
            }}>
              <Typography sx={{
                fontSize: '80pt', fontWeight: 900, color: 'rgba(0,0,0,0.06)',
                transform: 'rotate(-35deg)', userSelect: 'none', whiteSpace: 'nowrap',
                letterSpacing: '0.1em',
              }}>
                MINUTA
              </Typography>
            </Box>
          )}
          <Box 
            ref={editorContainerRef}
            sx={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
            transform: `scale(${zoom / 100})`,
            transformOrigin: 'top center',
            transition: 'transform 0.2s ease-in-out',

          '& .ProseMirror': {
            fontFamily: 'Times New Roman, serif !important',
            fontSize: '12pt !important',
            lineHeight: '1.5 !important',
            outline: 'none !important',

            '& p': {
              margin: '0 0 3px 0 !important',
              textAlign: 'justify !important',
              lineHeight: '1.5 !important',
              fontFamily: 'Times New Roman, serif !important',
              fontSize: '12pt !important',
            },
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              margin: '8px 0 !important',
              textAlign: 'center !important',
              lineHeight: '1.5 !important',
              fontFamily: 'Times New Roman, serif !important',
              fontSize: '12pt !important',
              fontWeight: 'bold !important',
            },
            '& img': {
              maxWidth: '100%',
              height: 'auto',
              display: 'block',
              margin: '16px auto',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.3)',
                transform: 'scale(1.01)',
              },
              '&:active': {
                transform: 'scale(0.99)',
              },
              '&.ProseMirror-selectednode': {
                outline: '2px solid #1976d2',
                outlineOffset: '2px',
              }
            }
          }
        }}>
          <EditorContent 
            editor={editor}
          />
          
          {editor && (
            <BubbleMenu 
              editor={editor}
              tippyOptions={{ duration: 100, maxWidth: 'none' }}
            >
              <Paper sx={{ 
                p: 1, 
                display: 'flex', 
                gap: 0.5, 
                alignItems: 'center',
                boxShadow: 3,
                borderRadius: 2,
                backgroundColor: 'white',
                overflow: 'visible',
                whiteSpace: 'nowrap'
              }}>
                {/* Formatação básica */}
                <ButtonGroup size="small" variant="text">
                  <Tooltip title="Negrito">
                    <IconButton
                      onClick={() => editor.chain().focus().toggleBold().run()}
                      color={editor.isActive('bold') ? 'primary' : 'default'}
                      size="small"
                    >
                      <FormatBold fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Itálico">
                    <IconButton
                      onClick={() => editor.chain().focus().toggleItalic().run()}
                      color={editor.isActive('italic') ? 'primary' : 'default'}
                      size="small"
                    >
                      <FormatItalic fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Sublinhado">
                    <IconButton
                      onClick={() => editor.chain().focus().toggleUnderline().run()}
                      color={editor.isActive('underline') ? 'primary' : 'default'}
                      size="small"
                    >
                      <FormatUnderlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ButtonGroup>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                {/* Cores rápidas */}
                <ButtonGroup size="small" variant="text">
                  <Tooltip title="Texto azul (jurídico)">
                    <IconButton
                      onClick={() => editor.chain().focus().setColor('#1976d2').run()}
                      size="small"
                    >
                      <Box sx={{ 
                        width: 16, 
                        height: 16, 
                        backgroundColor: '#1976d2', 
                        borderRadius: '2px',
                        border: '1px solid #ccc'
                      }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Texto vermelho (destaque)">
                    <IconButton
                      onClick={() => editor.chain().focus().setColor('#d32f2f').run()}
                      size="small"
                    >
                      <Box sx={{ 
                        width: 16, 
                        height: 16, 
                        backgroundColor: '#d32f2f', 
                        borderRadius: '2px',
                        border: '1px solid #ccc'
                      }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Destacar texto">
                    <IconButton
                      onClick={() => editor.chain().focus().toggleHighlight().run()}
                      color={editor.isActive('highlight') ? 'primary' : 'default'}
                      size="small"
                    >
                      <HighlightIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ButtonGroup>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                {/* Ferramentas avançadas */}
                <ButtonGroup size="small" variant="text">
                  <Tooltip title="Fazer citação">
                    <IconButton
                      onClick={() => editor.chain().focus().toggleBlockquote().run()}
                      color={editor.isActive('blockquote') ? 'primary' : 'default'}
                      size="small"
                    >
                      <FormatQuote fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Inserir link">
                    <IconButton
                      onClick={() => {
                        const url = prompt('Digite a URL:');
                        if (url) {
                          editor.chain().focus().setLink({ href: url }).run();
                        }
                      }}
                      size="small"
                    >
                      <LinkIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Inserir imagem">
                    <IconButton
                      onClick={() => setImageDialog(true)}
                      size="small"
                    >
                      <ImageIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Painel de campos">
                    <IconButton
                      onClick={() => setShowFieldsPanel(!showFieldsPanel)}
                      size="small"
                      color={showFieldsPanel ? "primary" : "default"}
                    >
                      <Template fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {/* Botão de Reescrita com IA */}
                  <Tooltip title="Reescrever com IA">
                    <IconButton
                      onClick={() => {
                        const selectedText = editor.state.doc.textBetween(
                          editor.state.selection.from,
                          editor.state.selection.to,
                          ' '
                        );
                        if (selectedText && selectedText.length >= 10) {
                          const from = Math.max(0, editor.state.selection.from - 200);
                          const to = Math.min(editor.state.doc.content.size, editor.state.selection.to + 200);
                          const context = editor.state.doc.textBetween(from, to, ' ');
                          setAiRewriteSelection({
                            text: selectedText,
                            context: context,
                            from: editor.state.selection.from,
                            to: editor.state.selection.to
                          });
                          setAiRewriteOpen(true);
                        } else {
                          setNotification({
                            open: true,
                            message: 'Selecione pelo menos 10 caracteres para reescrever',
                            severity: 'info'
                          });
                        }
                      }}
                      size="small"
                      sx={{ 
                        color: '#1a365d',
                        '&:hover': { 
                          backgroundColor: 'rgba(26, 54, 93, 0.1)',
                          color: '#2d5a87'
                        }
                      }}
                    >
                      <AIIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {/* Botão de Feedback dentro do ButtonGroup */}
                  {onFeedbackRequest && (
                    <Tooltip title="Avaliar este texto">
                      <IconButton
                        onClick={() => {
                          const selectedText = editor.state.doc.textBetween(
                            editor.state.selection.from,
                            editor.state.selection.to,
                            ' '
                          );
                          if (selectedText && selectedText.length >= 10) {
                            const from = Math.max(0, editor.state.selection.from - 100);
                            const to = Math.min(editor.state.doc.content.size, editor.state.selection.to + 100);
                            const context = editor.state.doc.textBetween(from, to, ' ');
                            onFeedbackRequest(selectedText, context);
                          }
                        }}
                        size="small"
                        sx={{ 
                          color: '#EB5D36',
                          '&:hover': { 
                            backgroundColor: 'rgba(235, 93, 54, 0.1)' 
                          }
                        }}
                      >
                        <StarIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </ButtonGroup>
              </Paper>
            </BubbleMenu>
          )}

          {/* FloatingMenu para linhas vazias */}
          {editor && (
            <FloatingMenu 
              editor={editor}
              tippyOptions={{ duration: 100 }}
            >
              <Paper sx={{ 
                p: 1, 
                display: 'flex', 
                gap: 1,
                boxShadow: 2,
                borderRadius: 1,
                backgroundColor: 'white'
              }}>
                <Tooltip title="Adicionar título">
                  <IconButton
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    size="small"
                  >
                    <TitleIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Adicionar lista">
                  <IconButton
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    size="small"
                  >
                    <FormatListBulleted fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Adicionar tabela">
                  <IconButton
                    onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
                    size="small"
                  >
                    <TableIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Adicionar citação">
                  <IconButton
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    size="small"
                  >
                    <FormatQuote fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Inserir imagem">
                  <IconButton
                    onClick={() => setImageDialog(true)}
                    size="small"
                  >
                    <ImageIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Paper>
            </FloatingMenu>
          )}
          </Box>
        </Box>

        {/* Document Outline Panel */}
        <DocumentOutline editor={editor} isOpen={showOutlinePanel} onClose={() => setShowOutlinePanel(false)} />

        {/* Legal Snippets Panel */}
        <LegalSnippetsPanel
          isOpen={showSnippetsPanel}
          onClose={() => setShowSnippetsPanel(false)}
          onInsert={(text) => {
            editor?.chain().focus().insertContent(text.replace(/\n/g, '<br>')).run()
            setNotification({ open: true, message: 'Texto inserido!', severity: 'success' })
          }}
        />
      </Box>

      {/* ──── Status bar ──── */}
      {editor && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 2,
          px: 2, py: 0.5, borderTop: '1px solid', borderColor: 'divider',
          backgroundColor: '#f8f9fa', flexShrink: 0, minHeight: 28,
        }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            {editor.storage.characterCount.characters().toLocaleString('pt-BR')} caracteres
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            {editor.storage.characterCount.words().toLocaleString('pt-BR')} palavras
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            ~{Math.max(1, Math.ceil(editor.storage.characterCount.words() / 200))} min leitura
          </Typography>
          <PageCounter editorContainer={editorContainerRef.current} />
          {(() => {
            if (placeholderCount === 0) return null
            return (
              <Tooltip title="Clique para ir ao próximo campo a preencher">
                <Chip
                  size="small"
                  color="warning"
                  label={`${placeholderCount} ${placeholderCount === 1 ? 'campo a preencher' : 'campos a preencher'}`}
                  onClick={() => goToNextPlaceholder()}
                  sx={{ height: 20, fontSize: '0.68rem', cursor: 'pointer' }}
                />
              </Tooltip>
            )
          })()}
          {wordTarget && (() => {
            const words = editor.storage.characterCount.words()
            const pct = Math.min(100, Math.round((words / wordTarget) * 100))
            const color = pct > 100 ? '#d32f2f' : pct > 85 ? '#f57c00' : '#388e3c'
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: '#e0e0e0', overflow: 'hidden' }}>
                  <Box sx={{ width: `${Math.min(100, pct)}%`, height: '100%', bgcolor: color, borderRadius: 2, transition: 'width 0.3s' }} />
                </Box>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color, fontWeight: 600 }}>
                  {words.toLocaleString('pt-BR')}/{wordTarget.toLocaleString('pt-BR')} ({pct}%)
                </Typography>
              </Box>
            )
          })()}
          {(() => {
            const { from, to } = editor.state.selection
            const selLen = to - from
            if (selLen > 0) {
              const selText = editor.state.doc.textBetween(from, to, ' ')
              const selWords = selText.trim().split(/\s+/).filter(Boolean).length
              return (
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#1976d2', fontWeight: 500 }}>
                  {selLen} caracteres selecionados ({selWords} palavras)
                </Typography>
              )
            }
            return null
          })()}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            {focusMode && (
              <Chip label="FOCO" size="small" onDelete={() => setFocusMode(false)} sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#1a365d', color: 'white', '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)', fontSize: 14 } }} />
            )}
            <Tooltip title="Atalhos de teclado (?)">
              <IconButton size="small" onClick={() => setShowShortcuts(true)} sx={{ p: '2px' }}>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', fontWeight: 600 }}>?</Typography>
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {/* Menu de cores */}
      <Menu
        anchorEl={colorMenuAnchor}
        open={Boolean(colorMenuAnchor)}
        onClose={() => setColorMenuAnchor(null)}
        PaperProps={{
          sx: { width: 200 }
        }}
      >
        <MenuList dense>
          <Typography variant="caption" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
            Cores do texto
          </Typography>
          <Divider />
          
          {/* Cores básicas */}
          <MenuItem onClick={() => { editor?.chain().focus().setColor('#000000').run(); setColorMenuAnchor(null); }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#000000', mr: 1, border: '1px solid #ccc' }} />
            Preto
          </MenuItem>
          <MenuItem onClick={() => { editor?.chain().focus().setColor('#424242').run(); setColorMenuAnchor(null); }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#424242', mr: 1, border: '1px solid #ccc' }} />
            Cinza escuro
          </MenuItem>
          <MenuItem onClick={() => { editor?.chain().focus().setColor('#757575').run(); setColorMenuAnchor(null); }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#757575', mr: 1, border: '1px solid #ccc' }} />
            Cinza
          </MenuItem>
          
          <Divider sx={{ my: 1 }} />
          
          {/* Cores jurídicas */}
          <MenuItem onClick={() => { editor?.chain().focus().setColor('#1976d2').run(); setColorMenuAnchor(null); }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#1976d2', mr: 1, border: '1px solid #ccc' }} />
            Azul jurídico
          </MenuItem>
          <MenuItem onClick={() => { editor?.chain().focus().setColor('#0d47a1').run(); setColorMenuAnchor(null); }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#0d47a1', mr: 1, border: '1px solid #ccc' }} />
            Azul escuro
          </MenuItem>
          <MenuItem onClick={() => { editor?.chain().focus().setColor('#1565c0').run(); setColorMenuAnchor(null); }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#1565c0', mr: 1, border: '1px solid #ccc' }} />
            Azul médio
          </MenuItem>
          
          <Divider sx={{ my: 1 }} />
          
          {/* Cores de destaque */}
          <MenuItem onClick={() => { editor?.chain().focus().setColor('#d32f2f').run(); setColorMenuAnchor(null); }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#d32f2f', mr: 1, border: '1px solid #ccc' }} />
            Vermelho
          </MenuItem>
          <MenuItem onClick={() => { editor?.chain().focus().setColor('#c62828').run(); setColorMenuAnchor(null); }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#c62828', mr: 1, border: '1px solid #ccc' }} />
            Vermelho escuro
          </MenuItem>
          <MenuItem onClick={() => { editor?.chain().focus().setColor('#388e3c').run(); setColorMenuAnchor(null); }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#388e3c', mr: 1, border: '1px solid #ccc' }} />
            Verde
          </MenuItem>
          <MenuItem onClick={() => { editor?.chain().focus().setColor('#2e7d32').run(); setColorMenuAnchor(null); }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#2e7d32', mr: 1, border: '1px solid #ccc' }} />
            Verde escuro
          </MenuItem>
          <MenuItem onClick={() => { editor?.chain().focus().setColor('#f57c00').run(); setColorMenuAnchor(null); }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#f57c00', mr: 1, border: '1px solid #ccc' }} />
            Laranja
          </MenuItem>
          <MenuItem onClick={() => { editor?.chain().focus().setColor('#ef6c00').run(); setColorMenuAnchor(null); }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#ef6c00', mr: 1, border: '1px solid #ccc' }} />
            Laranja escuro
          </MenuItem>
          <MenuItem onClick={() => { editor?.chain().focus().setColor('#7b1fa2').run(); setColorMenuAnchor(null); }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#7b1fa2', mr: 1, border: '1px solid #ccc' }} />
            Roxo
          </MenuItem>
          <MenuItem onClick={() => { editor?.chain().focus().setColor('#5d4037').run(); setColorMenuAnchor(null); }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#5d4037', mr: 1, border: '1px solid #ccc' }} />
            Marrom
          </MenuItem>
        </MenuList>
      </Menu>

      {/* Dialog para preencher campos */}
      <Dialog 
        open={fieldsDialog}
        onClose={() => setFieldsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Template color="primary" />
            Preencher Campos do Documento
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Encontramos {documentFields.length} campos para preencher no documento. 
            Complete as informações abaixo:
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {documentFields.map((field, index) => (
              <TextField
                key={index}
                label={field.placeholder}
                value={fieldValues[field.placeholder] || ''}
                onChange={(e) => setFieldValues(prev => ({
                  ...prev,
                  [field.placeholder]: e.target.value
                }))}
                fullWidth
                required={field.required}
                variant="outlined"
                size="small"
                helperText={field.required ? 'Campo obrigatório' : 'Campo opcional'}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setFieldsDialog(false)}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button 
            onClick={() => {
              handleFieldUpdate(fieldValues);
              setFieldsDialog(false);
            }}
            variant="contained"
            color="primary"
          >
            Aplicar Campos
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para inserir imagem */}
      <Dialog 
        open={imageDialog}
        onClose={() => {
          setImageDialog(false)
          setImageUrl('')
          setImageFile(null)
          setImagePreview(null)
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImageIcon color="primary" />
            Inserir Imagem
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Insira uma imagem por URL ou faça upload de um arquivo do seu computador.
          </Typography>
          
          {/* Upload de arquivo */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Upload de Arquivo
            </Typography>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ 
                py: 2, 
                borderStyle: 'dashed',
                borderColor: imageFile ? 'success.main' : 'divider'
              }}
            >
              {imageFile ? imageFile.name : 'Clique para selecionar uma imagem'}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) {
                    setImageFile(file)
                    setImageUrl('')
                    const reader = new FileReader()
                    reader.onload = (event) => {
                      setImagePreview(event.target?.result as string)
                    }
                    reader.readAsDataURL(file)
                  }
                }}
              />
            </Button>
          </Box>
          
          <Divider sx={{ my: 2 }}>
            <Typography variant="caption" color="text.secondary">ou</Typography>
          </Divider>
          
          {/* URL da imagem */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              URL da Imagem
            </Typography>
            <TextField
              fullWidth
              placeholder="https://exemplo.com/imagem.jpg"
              value={imageUrl}
              onChange={(e) => {
                setImageUrl(e.target.value)
                setImageFile(null)
                setImagePreview(e.target.value || null)
              }}
              size="small"
            />
          </Box>
          
          {/* Preview da imagem */}
          {imagePreview && (
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              backgroundColor: '#f5f5f5',
              textAlign: 'center'
            }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Preview:
              </Typography>
              <Box
                component="img"
                src={imagePreview}
                alt="Preview"
                sx={{
                  maxWidth: '100%',
                  maxHeight: 200,
                  objectFit: 'contain',
                  borderRadius: 1
                }}
                onError={() => {
                  setNotification({
                    open: true,
                    message: 'Erro ao carregar a imagem. Verifique a URL.',
                    severity: 'error'
                  })
                  setImagePreview(null)
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setImageDialog(false)
              setImageUrl('')
              setImageFile(null)
              setImagePreview(null)
            }}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button 
            onClick={() => {
              if (imagePreview) {
                editor?.chain().focus().setImage({ src: imagePreview }).run()
                setNotification({
                  open: true,
                  message: 'Imagem inserida com sucesso!',
                  severity: 'success'
                })
              }
              setImageDialog(false)
              setImageUrl('')
              setImageFile(null)
              setImagePreview(null)
            }}
            variant="contained"
            color="primary"
            disabled={!imagePreview}
          >
            Inserir Imagem
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para editar imagem */}
      <Dialog 
        open={editImageDialog}
        onClose={() => {
          setEditImageDialog(false)
          setSelectedImage(null)
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditImageIcon color="primary" />
            Editar Imagem
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedImage && (
            <Stack spacing={3}>
              {/* Preview da imagem */}
              <Box sx={{ 
                textAlign: 'center', 
                p: 2, 
                bgcolor: '#f5f5f5', 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Box
                  component="img"
                  src={selectedImage.src}
                  alt="Preview"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 200,
                    width: imageWidth,
                    height: imageHeight,
                    objectFit: 'contain',
                    borderRadius: 1,
                    transition: 'all 0.2s'
                  }}
                />
              </Box>

              {/* Dimensões */}
              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AspectRatio fontSize="small" />
                  Dimensões
                </Typography>
                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={maintainAspectRatio}
                      onChange={(e) => setMaintainAspectRatio(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Manter proporção"
                  sx={{ mb: 2 }}
                />
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Largura (px)
                    </Typography>
                    <TextField
                      type="number"
                      value={imageWidth}
                      onChange={(e) => handleWidthChange(Number(e.target.value))}
                      size="small"
                      fullWidth
                      inputProps={{ min: 50, max: 800 }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Altura (px)
                    </Typography>
                    <TextField
                      type="number"
                      value={imageHeight}
                      onChange={(e) => handleHeightChange(Number(e.target.value))}
                      size="small"
                      fullWidth
                      inputProps={{ min: 50, max: 800 }}
                    />
                  </Box>
                </Box>
                
                {/* Slider de escala */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Escala rápida
                  </Typography>
                  <Slider
                    value={Math.round((imageWidth / selectedImage.originalWidth) * 100)}
                    onChange={(_, value) => {
                      const scale = (value as number) / 100
                      setImageWidth(Math.round(selectedImage.originalWidth * scale))
                      setImageHeight(Math.round(selectedImage.originalHeight * scale))
                    }}
                    min={10}
                    max={100}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${value}%`}
                    marks={[
                      { value: 25, label: '25%' },
                      { value: 50, label: '50%' },
                      { value: 75, label: '75%' },
                      { value: 100, label: '100%' },
                    ]}
                  />
                </Box>
              </Box>

              {/* Alinhamento */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Alinhamento
                </Typography>
                <ToggleButtonGroup
                  value={imageAlignment}
                  exclusive
                  onChange={(_, value) => value && setImageAlignment(value)}
                  size="small"
                  fullWidth
                >
                  <ToggleButton value="left">
                    <FormatAlignLeft sx={{ mr: 0.5 }} fontSize="small" />
                    Esquerda
                  </ToggleButton>
                  <ToggleButton value="center">
                    <FormatAlignCenter sx={{ mr: 0.5 }} fontSize="small" />
                    Centro
                  </ToggleButton>
                  <ToggleButton value="right">
                    <FormatAlignRight sx={{ mr: 0.5 }} fontSize="small" />
                    Direita
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Info da imagem */}
              <Box sx={{ 
                p: 1.5, 
                bgcolor: 'grey.100', 
                borderRadius: 1,
                fontSize: '0.75rem'
              }}>
                <Typography variant="caption" color="text.secondary">
                  Tamanho original: {selectedImage.originalWidth} × {selectedImage.originalHeight} px
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Button 
            onClick={removeImage}
            color="error"
            startIcon={<DeleteIcon />}
          >
            Remover
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              onClick={() => {
                setEditImageDialog(false)
                setSelectedImage(null)
              }}
              color="inherit"
            >
              Cancelar
            </Button>
            <Button 
              onClick={applyImageChanges}
              variant="contained"
              color="primary"
            >
              Aplicar
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Painel lateral de campos */}
      <FieldsPanel
        isOpen={showFieldsPanel}
        onClose={() => setShowFieldsPanel(false)}
        documentContent={content}
        onFieldUpdate={handleFieldUpdate}
      />

      {/* Painel lateral de jurisprudência */}
      <SimplifiedJurisprudencePanel
        isOpen={jurisprudenceDialog}
        onClose={() => setJurisprudenceDialog(false)}
        onInsertJurisprudence={handleInsertJurisprudenceText}
      />

      {/* Popover de Reescrita com IA */}
      <Dialog
        open={aiRewriteOpen}
        onClose={() => {
          setAiRewriteOpen(false);
          setAiRewriteSelection(null);
        }}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            margin: 2,
            maxWidth: 480,
            width: '100%',
          }
        }}
      >
        {aiRewriteSelection && (
          <AIRewritePopover
            selectedText={aiRewriteSelection.text}
            context={aiRewriteSelection.context}
            onRewrite={(newText) => {
              if (editor && aiRewriteSelection) {
                // Substituir o texto selecionado pelo novo texto
                editor
                  .chain()
                  .focus()
                  .setTextSelection({
                    from: aiRewriteSelection.from,
                    to: aiRewriteSelection.to
                  })
                  .deleteSelection()
                  .insertContent(newText)
                  .run();
                
                setNotification({
                  open: true,
                  message: 'Texto reescrito com sucesso!',
                  severity: 'success'
                });
              }
            }}
            onClose={() => {
              setAiRewriteOpen(false);
              setAiRewriteSelection(null);
            }}
          />
        )}
      </Dialog>

      {/* Dialog de Reescrita Completa do Documento */}
      <AIFullRewriteDialog
        open={aiFullRewriteOpen}
        onClose={() => setAiFullRewriteOpen(false)}
        documentContent={content}
        documentTitle={documentId}
        onRewrite={(newContent, createNew) => {
          if (createNew) {
            // Abrir novo documento em nova aba
            const newWindow = window.open('/documents/new', '_blank');
            if (newWindow) {
              // Salvar o conteúdo no localStorage para a nova página recuperar
              localStorage.setItem('newDocumentContent', newContent);
              localStorage.setItem('newDocumentSource', 'ai_rewrite');
            }
            setNotification({
              open: true,
              message: 'Novo documento criado em nova aba!',
              severity: 'success'
            });
          } else {
            // Substituir o conteúdo atual
            if (editor) {
              editor.commands.setContent(newContent);
              onContentChange(newContent);
              setNotification({
                open: true,
                message: 'Documento reescrito com sucesso!',
                severity: 'success'
              });
            }
          }
        }}
      />

      {/* Header / Footer Config Dialog */}
      <Dialog open={headerFooterDialog} onClose={() => setHeaderFooterDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: 15 }}>Cabeçalho e Rodapé</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: 12 }}>
            Configure o cabeçalho e rodapé do documento. Use {'{page}'} para número da página.
          </Typography>
          <TextField fullWidth size="small" label="Cabeçalho" value={customHeader}
            onChange={(e) => setCustomHeader(e.target.value)}
            placeholder="Ex: Processo nº 1234-56.2025.8.26.0100"
            sx={{ mb: 2 }} />
          <TextField fullWidth size="small" label="Rodapé" value={customFooter}
            onChange={(e) => setCustomFooter(e.target.value)}
            placeholder="Ex: Página {page}" sx={{ mb: 2 }} />
          <FormControlLabel
            control={<Checkbox checked={firstPageDifferent} onChange={(e) => setFirstPageDifferent(e.target.checked)} size="small" />}
            label={<Typography variant="body2" sx={{ fontSize: 12 }}>Primeira página sem cabeçalho</Typography>}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHeaderFooterDialog(false)} color="inherit">Cancelar</Button>
          <Button variant="contained" onClick={() => {
            if (editor) {
              const headerHtml = customHeader ? `<span style="font-family: Arial, sans-serif; font-size: 9pt; color: #888;">${customHeader}</span>` : ''
              const footerHtml = customFooter ? `<span style="font-family: Arial, sans-serif; font-size: 9pt; color: #888; display:block; text-align:center; width:100%;">${customFooter}</span>` : ''
              editor.chain().focus()
                .updateHeaderContent(firstPageDifferent ? '' : headerHtml, '')
                .updateFooterContent(footerHtml, '')
                .run()
              if (firstPageDifferent) {
                editor.chain().focus().updateHeaderContent('', '', 1).run()
              }
            }
            setHeaderFooterDialog(false)
            setNotification({ open: true, message: 'Cabeçalho e rodapé atualizados!', severity: 'success' })
          }}>Aplicar</Button>
        </DialogActions>
      </Dialog>

      {/* Keyboard Shortcuts Overlay */}
      <Dialog open={showShortcuts} onClose={() => setShowShortcuts(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 16 }}>Atalhos de Teclado</Typography>
          <IconButton size="small" onClick={() => setShowShortcuts(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            {[
              { section: 'Formatação', shortcuts: [
                { keys: 'Ctrl + B', desc: 'Negrito' }, { keys: 'Ctrl + I', desc: 'Itálico' },
                { keys: 'Ctrl + U', desc: 'Sublinhado' }, { keys: 'Ctrl + Shift + X', desc: 'Tachado' },
                { keys: 'Ctrl + .', desc: 'Sobrescrito' }, { keys: 'Ctrl + ,', desc: 'Subscrito' },
              ]},
              { section: 'Edição', shortcuts: [
                { keys: 'Ctrl + Z', desc: 'Desfazer' }, { keys: 'Ctrl + Y', desc: 'Refazer' },
                { keys: 'Ctrl + F', desc: 'Buscar' }, { keys: 'Ctrl + H', desc: 'Substituir' },
                { keys: 'Ctrl + A', desc: 'Selecionar tudo' },
              ]},
              { section: 'Estrutura', shortcuts: [
                { keys: 'Ctrl + Shift + 1', desc: 'Título 1' }, { keys: 'Ctrl + Shift + 2', desc: 'Título 2' },
                { keys: 'Ctrl + Shift + 3', desc: 'Título 3' }, { keys: 'Ctrl + Shift + 8', desc: 'Lista marcadores' },
                { keys: 'Ctrl + Shift + 9', desc: 'Lista numerada' },
              ]},
              { section: 'Ações', shortcuts: [
                { keys: 'Ctrl + S', desc: 'Salvar' }, { keys: 'Ctrl + P', desc: 'Imprimir' },
                { keys: 'Esc', desc: 'Sair do modo foco' },
              ]},
            ].map(group => (
              <Box key={group.section}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#1a365d', fontSize: 11, display: 'block', mb: 0.5 }}>
                  {group.section}
                </Typography>
                {group.shortcuts.map(s => (
                  <Box key={s.keys} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.3 }}>
                    <Typography variant="caption" sx={{ fontSize: 11.5 }}>{s.desc}</Typography>
                    <Chip label={s.keys} size="small" variant="outlined"
                      sx={{ height: 20, fontSize: '0.6rem', fontFamily: 'monospace', borderColor: '#e0e0e0' }} />
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Notificações */}
      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default SimpleLegalEditor 